import React, { useEffect, useState, useMemo, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, animate } from "framer-motion";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  ArrowRight,
  ShoppingCart,
  Cards,
  Users,
  Clock,
  Baby,
  Lightning,
  MaskHappy,
  Star,
  Question,
  Eye,
  Stack,
  Package,
  Fire,
  CheckCircle,
  TiktokLogo,
  InstagramLogo,
  YoutubeLogo,
  DiscordLogo,
  XLogo,
  List,
  X as CloseIcon,
  Play,
  PaperPlaneTilt,
  CaretLeft,
  CaretRight,
  Heart,
  ChatCircleDots,
  PaperPlaneRight,
  BookmarkSimple,
  DotsThreeVertical,
} from "@phosphor-icons/react";
import { useCart } from "@/lib/CartContext";
import { Magnetic } from "@/lib/motion";
import { AsteriskMark } from "@/components/AsteriskMark";

// ------- Assets -------
const BOX_IMG =
  "/assets/box-cinematic.png";
const BUNDLE_IMG =
  "/assets/bundle-1.png";
const ORIGINAL_QUAD_IMG =
  "/assets/original-quad.png";
const HOST_IMG =
  "/hero-banner-image.webp";
const HOST_LANDSCAPE_IMG =
  "/hero-banner-landscape.webp";
const LOGO_IMG =
  "/assets/hero-logo.png";
const WOE_LOGO =
  "/assets/woe-logo.png";
const CARD_A = "/assets/deck-1.png";
const CARD_B = "/assets/deck-2.png";
const CARD_C = "/assets/deck-3.png";
const CARD_D = "/assets/deck-4.png";
// Real UGC - game-night photos of people playing House Rules
const UGC_A = "/assets/ugc-a.png";
const UGC_B = "/assets/ugc-b.png";
const UGC_C = "/assets/ugc-c.png";
const UGC_D = "/assets/ugc-d.jpg";
// Real-life game night photos (customer submissions)
const PHOTO_A = "/assets/photo-a.jpg";
const PHOTO_B = "/assets/photo-b.jpg";
const PHOTO_C = "/assets/photo-c.jpg";
const PHOTO_D = "/assets/photo-d.jpg";
const PHOTO_E = "/assets/photo-e.jpg";
const PHOTO_F = "/assets/photo-f.jpg";

const NAV = [
  { href: "#shop", label: "Shop" },
  { href: "#how", label: "How to Play" },
  { href: "#host", label: "About" },
  { href: "#shop", label: "Bundles" },
  { href: "#faq", label: "FAQ" },
];

// Deck data - per mockup, each with color-coded panel
const DECKS = [
  {
    key: "physical",
    name: "Physical",
    icon: Lightning,
    prompt: "Get off the couch.",
    from: "#5A22C9",
    to: "#2A0E7A",
    glow: "123, 44, 255",
    accent: "#B57BFF",
    img: CARD_A,
  },
  {
    key: "charades",
    name: "House Charades",
    icon: Star,
    prompt: "Act it out, don't say it.",
    from: "#FF3B18",
    to: "#8A1500",
    glow: "255, 59, 24",
    accent: "#FFB44E",
    img: CARD_B,
  },
  {
    key: "know",
    name: "Know Your House",
    icon: Question,
    prompt: "How well do you know your people?",
    from: "#1E3EA8",
    to: "#0A1440",
    glow: "30, 62, 168",
    accent: "#6DA6FF",
    img: CARD_C,
  },
  {
    key: "truth",
    name: "Truth or Lie",
    icon: Star,
    prompt: "Spill it or make it up.",
    from: "#E01F7C",
    to: "#780838",
    glow: "224, 31, 124",
    accent: "#FF7EBB",
    img: CARD_D,
  },
];

const fmt = (n) => `$${Number(n || 0).toFixed(0)}`;

// -------- Small primitives --------
const Reveal = ({ children, delay = 0, className = "" }) => (
  <motion.div
    initial={{ opacity: 0, y: 24 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-80px" }}
    transition={{ duration: 0.75, delay, ease: [0.22, 1, 0.36, 1] }}
    className={className}
  >
    {children}
  </motion.div>
);

// -------- Cursor glow --------
function CursorGlow() {
  const ref = useRef(null);
  useEffect(() => {
    if (window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const onMove = (e) => {
      if (!ref.current) return;
      ref.current.style.left = `${e.clientX}px`;
      ref.current.style.top = `${e.clientY}px`;
    };
    window.addEventListener("mousemove", onMove);
    return () => window.removeEventListener("mousemove", onMove);
  }, []);
  return <div ref={ref} className="hr-cursor-glow" aria-hidden />;
}

// -------- Animated counter --------
function Counter({ to, prefix = "", suffix = "" }) {
  const ref = useRef(null);
  const [val, setVal] = useState(0);
  useEffect(() => {
    const obs = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) {
          const controls = animate(0, to, {
            duration: 1.4,
            ease: [0.22, 1, 0.36, 1],
            onUpdate: (v) => setVal(Math.floor(v)),
          });
          obs.disconnect();
          return () => controls.stop();
        }
      });
    }, { threshold: 0.3 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [to]);
  return <span ref={ref}>{prefix}{val}{suffix}</span>;
}

// -------- Top ticker --------
function TopStrip() {
  const items = ["The Multi-Occasional Game Box", "As Seen on Tubi", "Hosted by Laro Benz", "Free US Shipping", "Ships in 24 Hours"];
  return (
    <div className="relative border-b border-white/8 bg-black/40 overflow-hidden">
      <div className="flex items-center gap-14 py-2.5 whitespace-nowrap hr-marquee">
        {[...items, ...items, ...items].map((t, i) => (
          <span key={i} className="font-mono text-[10px] uppercase tracking-[0.35em] text-white/55 flex items-center gap-4">
            <span className="text-brandpink">*</span> {t}
          </span>
        ))}
      </div>
    </div>
  );
}

// -------- Nav --------
function Nav({ onOpenCart, onOpenMenu, count, onBuyNow, price }) {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return (
    <header
      className={`sticky top-0 z-40 transition-all duration-300 ${
        scrolled ? "bg-[#080412]/80 backdrop-blur-xl border-b border-white/8" : "bg-transparent"
      }`}
      data-testid="navbar"
    >
      <div className="max-w-[1400px] mx-auto flex items-center justify-between px-4 md:px-8 h-16 md:h-24 py-2">
        <a href="#top" className="flex items-center" data-testid="brand-logo">
          <img src={LOGO_IMG} alt="House Rules" className="h-11 sm:h-14 md:h-[72px] lg:h-20 w-auto rounded-lg drop-shadow-[0_4px_24px_rgba(167,66,255,0.5)]" />
        </a>
        <nav className="hidden lg:flex items-center gap-9 font-sora text-[12px] uppercase tracking-[0.18em] font-medium text-white/70" aria-label="Primary">
          {NAV.map((n, i) => (
            <a key={i} href={n.href} className="hover:text-white transition-colors">
              {n.label}
            </a>
          ))}
        </nav>
        <div className="flex items-center gap-2 md:gap-3">
          <button
            onClick={onOpenCart}
            className="relative h-11 w-11 grid place-items-center rounded-full hr-outline text-white/85 hover:text-white"
            data-testid="nav-cart-button"
            aria-label="Open cart"
          >
            <ShoppingCart size={17} weight="fill" />
            <span
              className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-brandpink text-white text-[10px] font-mono grid place-items-center leading-none"
              data-testid="cart-count"
            >
              {count}
            </span>
          </button>
          <Magnetic strength={0.22} className="hidden sm:inline-block">
            <button
              onClick={onBuyNow}
              className="hr-cta h-11 px-5 rounded-full text-white font-sora text-[11px] uppercase tracking-[0.18em] font-semibold flex items-center gap-2"
              data-testid="nav-buy-now-button"
            >
              Buy Now - {fmt(price)}
            </button>
          </Magnetic>
          <button
            onClick={onOpenMenu}
            className="lg:hidden h-11 w-11 grid place-items-center rounded-full hr-outline text-white"
            data-testid="nav-menu-button"
            aria-label="Open menu"
          >
            <List size={18} weight="bold" />
          </button>
        </div>
      </div>
    </header>
  );
}

function MobileMenu({ open, onClose }) {
  useEffect(() => {
    if (open) {
      const p = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = p; };
    }
  }, [open]);
  return (
    <div className={`fixed inset-0 z-[55] ${open ? "pointer-events-auto" : "pointer-events-none"}`} aria-hidden={!open}>
      <div onClick={onClose} className={`absolute inset-0 bg-[#080412]/95 backdrop-blur-2xl transition-opacity duration-300 ${open ? "opacity-100" : "opacity-0"}`} />
      <div className={`absolute top-0 left-0 right-0 bg-[#120726] border-b border-white/10 transition-transform duration-500 ${open ? "translate-y-0" : "-translate-y-full"}`}>
        <div className="flex items-center justify-between px-5 h-16 border-b border-white/10">
          <img src={LOGO_IMG} alt="House Rules" className="h-9 w-auto rounded-md" />
          <button onClick={onClose} className="h-10 w-10 grid place-items-center rounded-full hr-outline" aria-label="Close"><CloseIcon size={16} weight="bold" /></button>
        </div>
        <nav className="px-6 py-10 flex flex-col gap-6 font-display uppercase text-4xl tracking-tight leading-none">
          {NAV.map((n, i) => (
            <a key={i} href={n.href} onClick={onClose} className="hover:text-brandpink transition-colors">{n.label}</a>
          ))}
          <Link to="/orders/track" onClick={onClose} className="hover:text-brandpink transition-colors">Track Order</Link>
        </nav>
      </div>
    </div>
  );
}

// -------- Hero --------
function Hero({ onAddToCart, onWatch, price }) {
  const { scrollY } = useScroll();
  const yFar = useTransform(scrollY, [0, 800], [0, 80]);
  const yMid = useTransform(scrollY, [0, 800], [0, 40]);
  const opacityFade = useTransform(scrollY, [0, 500], [1, 0.6]);

  return (
    <section id="top" className="relative overflow-hidden">
      {/* Ambient glow orbs */}
      <motion.div style={{ y: yFar, opacity: opacityFade }} className="absolute inset-0 pointer-events-none" aria-hidden>
        <div className="absolute top-20 left-1/4 w-[520px] h-[520px] rounded-full hr-pulse" style={{ background: "radial-gradient(circle, rgba(123,44,255,0.5), transparent 70%)" }} />
        <div className="absolute top-40 right-1/4 w-[460px] h-[460px] rounded-full hr-pulse" style={{ background: "radial-gradient(circle, rgba(255,44,140,0.35), transparent 70%)", animationDelay: "1.5s" }} />
        <div className="absolute bottom-10 left-1/2 w-[400px] h-[400px] rounded-full hr-pulse" style={{ background: "radial-gradient(circle, rgba(41,108,255,0.3), transparent 70%)", animationDelay: "3s" }} />
      </motion.div>

      <div className="relative max-w-[1400px] mx-auto px-4 md:px-8 pt-4 sm:pt-6 md:pt-10 pb-8 md:pb-16 grid md:grid-cols-12 gap-4 md:gap-8 lg:gap-10 items-center lg:hidden">
        {/* Left: Content */}
        <div className="md:col-span-6 relative z-10">
          <Reveal>
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 md:px-4 md:py-2 rounded-full hr-glass font-sora text-[10px] md:text-[10.5px] uppercase tracking-[0.28em] text-white/80">
              <span className="w-1.5 h-1.5 rounded-full bg-brandpink animate-pulse" />
              The Multi-Occasional Game Box
            </div>
          </Reveal>

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="mt-4 md:mt-7 font-display uppercase leading-[0.82] md:leading-[0.86] tracking-[-0.02em]"
          >
            <span className="block text-[13vw] sm:text-[11vw] md:text-[8.5vw] lg:text-[6.5rem] xl:text-[8rem]">
              <span className="text-white">Play By </span><span className="text-brandblue hr-text-glow-blue">Our</span>
            </span>
            <span className="block text-[13vw] sm:text-[11vw] md:text-[8.5vw] lg:text-[6.5rem] xl:text-[8rem]">
              <span className="italic font-editorial text-brandpink hr-text-glow-pink">House</span>
              <span className="text-white"> Rules.</span>
            </span>
          </motion.h1>

          <Reveal delay={0.2}>
            <p className="mt-3 md:mt-7 max-w-md text-sm md:text-lg font-sans text-white/70 leading-relaxed">
              200+ cards for every gathering you secretly love to ruin.
            </p>
          </Reveal>

          <Reveal delay={0.3}>
            <div className="mt-4 md:mt-8 flex flex-wrap items-center gap-2.5 md:gap-3">
              <Magnetic strength={0.24}>
                <button
                  onClick={onAddToCart}
                  className="hr-cta h-11 md:h-14 pl-4 pr-4 md:pl-6 md:pr-6 rounded-full text-white font-sora font-semibold text-[11px] md:text-sm uppercase tracking-[0.16em] md:tracking-[0.18em] flex items-center gap-2 md:gap-3 whitespace-nowrap"
                  data-testid="hero-add-to-cart-button"
                >
                  <ShoppingCart size={14} weight="fill" />
                  Buy Now - {fmt(price)}
                </button>
              </Magnetic>
              <button
                onClick={onWatch}
                className="hr-outline h-11 md:h-14 pl-4 pr-2.5 md:pl-6 md:pr-4 rounded-full text-white/95 font-sora font-semibold text-[11px] md:text-sm uppercase tracking-[0.16em] md:tracking-[0.18em] flex items-center gap-2 md:gap-3 whitespace-nowrap"
                data-testid="hero-watch-link"
              >
                Watch Trailer
                <span
                  className="h-7 w-7 md:h-9 md:w-9 rounded-full grid place-items-center shrink-0"
                  style={{ backgroundColor: "#FFFFFF", color: "#080412" }}
                >
                  <Play size={12} weight="fill" style={{ marginLeft: "1px" }} />
                </span>
              </button>
            </div>
          </Reveal>

          <Reveal delay={0.4}>
            <div className="mt-5 md:mt-11 flex flex-wrap items-center gap-x-4 gap-y-2 md:gap-x-6 md:gap-y-3 font-sora text-[9.5px] md:text-[10.5px] uppercase tracking-[0.22em] md:tracking-[0.25em] text-white/70">
              <span className="flex items-center gap-1.5 md:gap-2"><Cards size={14} weight="duotone" className="text-brandpink" /> 200+ Cards</span>
              <span className="flex items-center gap-1.5 md:gap-2"><Users size={14} weight="duotone" className="text-brandpink" /> 2+ Players</span>
              <span className="flex items-center gap-1.5 md:gap-2"><Baby size={14} weight="duotone" className="text-brandpink" /> Ages 8+</span>
              <span className="flex items-center gap-1.5 md:gap-2"><Clock size={14} weight="duotone" className="text-brandpink" /> 15–60 Min</span>
              <span className="hidden sm:flex items-center gap-1.5 md:gap-2"><Package size={14} weight="duotone" className="text-brandpink" /> Free US Shipping</span>
            </div>
          </Reveal>
        </div>

        {/* Right: cinematic hero banner (image already contains floating cards + host + stage) */}
        <motion.div style={{ y: yMid }} className="md:col-span-6 relative z-10 mt-3 md:mt-0">
          <div className="relative aspect-[3/2] sm:aspect-[16/10] md:aspect-[5/4] max-w-[640px] mx-auto">
            <div className="absolute inset-0 rounded-2xl md:rounded-3xl overflow-hidden border border-white/10 hr-neon-purple">
              <img
                src={HOST_IMG}
                alt="House Rules - hosted by Laro Benz with floating deck cards"
                className="absolute inset-0 w-full h-full object-cover object-center"
                loading="eager"
                fetchPriority="high"
              />
              {/* subtle bottom vignette for text legibility if any overlay */}
              <div className="absolute inset-x-0 bottom-0 h-1/3" style={{ background: "linear-gradient(180deg, transparent, rgba(8,4,18,0.6))" }} />
            </div>

            {/* Purple smoke particles */}
            <div aria-hidden className="absolute inset-0 pointer-events-none">
              {[...Array(10)].map((_, i) => (
                <span
                  key={i}
                  className="absolute rounded-full hr-pulse"
                  style={{
                    left: `${(i * 41) % 100}%`,
                    top: `${(i * 57) % 100}%`,
                    width: `${3 + (i % 3)}px`,
                    height: `${3 + (i % 3)}px`,
                    background: i % 2 === 0 ? "rgba(167,66,255,0.6)" : "rgba(255,44,140,0.45)",
                    filter: "blur(1px)",
                    animationDelay: `${i * 0.35}s`,
                  }}
                />
              ))}
            </div>
          </div>
        </motion.div>
      </div>

      {/* ---- DESKTOP / LANDSCAPE LAYOUT (lg+) - full-width cinematic banner ---- */}
      <div className="hidden lg:block relative">
        <div className="relative w-full min-h-[680px] xl:min-h-[780px] 2xl:min-h-[860px]">
          {/* Landscape hero image as background */}
          <img
            src={HOST_LANDSCAPE_IMG}
            alt="House Rules - hosted by Laro Benz on stage"
            className="absolute inset-0 w-full h-full object-cover object-center"
            loading="eager"
            fetchPriority="high"
          />
          {/* Left-side gradient overlay for copy legibility */}
          <div aria-hidden className="absolute inset-0" style={{
            background: "linear-gradient(90deg, rgba(8,4,18,0.96) 0%, rgba(8,4,18,0.78) 35%, rgba(8,4,18,0.25) 58%, transparent 85%)",
          }} />
          {/* Bottom vignette to blend into next section */}
          <div aria-hidden className="absolute inset-x-0 bottom-0 h-40 pointer-events-none" style={{
            background: "linear-gradient(180deg, transparent, rgba(8,4,18,1))",
          }} />

          {/* Overlay copy anchored to left */}
          <div className="relative max-w-[1400px] mx-auto px-8 pt-16 pb-24 grid grid-cols-12 items-center min-h-[680px] xl:min-h-[780px] 2xl:min-h-[860px]">
            <motion.div style={{ y: yMid }} className="col-span-7 xl:col-span-6 relative z-10">
              <Reveal>
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full hr-glass font-sora text-[10.5px] uppercase tracking-[0.28em] text-white/85">
                  <span className="w-1.5 h-1.5 rounded-full bg-brandpink animate-pulse" />
                  The Multi-Occasional Game Box
                </div>
              </Reveal>

              <motion.h1
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                className="mt-7 font-display uppercase leading-[0.86] tracking-[-0.02em]"
              >
                <span className="block text-[6vw] xl:text-[7.5rem] 2xl:text-[8.5rem]">
                  <span className="text-white">Play By </span><span className="text-brandblue hr-text-glow-blue">Our</span>
                </span>
                <span className="block text-[6vw] xl:text-[7.5rem] 2xl:text-[8.5rem]">
                  <span className="italic font-editorial text-brandpink hr-text-glow-pink">House</span>
                  <span className="text-white"> Rules.</span>
                </span>
              </motion.h1>

              <Reveal delay={0.2}>
                <p className="mt-6 max-w-md text-lg font-sans text-white/80 leading-relaxed">
                  200+ cards for every gathering you secretly love to ruin.
                </p>
              </Reveal>

              <Reveal delay={0.3}>
                <div className="mt-8 flex flex-wrap items-center gap-3">
                  <Magnetic strength={0.24}>
                    <button
                      onClick={onAddToCart}
                      className="hr-cta h-14 pl-6 pr-6 rounded-full text-white font-sora font-semibold text-sm uppercase tracking-[0.18em] flex items-center gap-3"
                      data-testid="hero-add-to-cart-button"
                    >
                      <ShoppingCart size={16} weight="fill" />
                      Buy Now - {fmt(price)}
                    </button>
                  </Magnetic>
                  <button
                    onClick={onWatch}
                    className="hr-outline h-14 pl-6 pr-4 rounded-full text-white/95 font-sora font-semibold text-sm uppercase tracking-[0.18em] flex items-center gap-3"
                    data-testid="hero-watch-link"
                  >
                    Watch Trailer
                    <span
                      className="h-9 w-9 rounded-full grid place-items-center shrink-0"
                      style={{ backgroundColor: "#FFFFFF", color: "#080412" }}
                    >
                      <Play size={14} weight="fill" style={{ marginLeft: "1px" }} />
                    </span>
                  </button>
                </div>
              </Reveal>

              <Reveal delay={0.4}>
                <div className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-3 font-sora text-[10.5px] uppercase tracking-[0.25em] text-white/75">
                  <span className="flex items-center gap-2"><Cards size={16} weight="duotone" className="text-brandpink" /> 200+ Cards</span>
                  <span className="flex items-center gap-2"><Users size={16} weight="duotone" className="text-brandpink" /> 2+ Players</span>
                  <span className="flex items-center gap-2"><Baby size={16} weight="duotone" className="text-brandpink" /> Ages 8+</span>
                  <span className="flex items-center gap-2"><Clock size={16} weight="duotone" className="text-brandpink" /> 15–60 Min</span>
                  <span className="flex items-center gap-2"><Package size={16} weight="duotone" className="text-brandpink" /> Free US Shipping</span>
                </div>
              </Reveal>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}

// -------- Why Everyone Loves It - counters --------
function WhyEveryoneLoves() {
  const items = [
    { icon: Cards, glow: "hr-neon-purple", value: 200, prefix: "", suffix: "+", label: "Premium Cards", accent: "#7B2CFF", type: "number" },
    { icon: Stack, glow: "hr-neon-blue", value: 4, prefix: "", suffix: "", label: "Unique Decks", accent: "#296CFF", type: "number" },
    { icon: Clock, glow: "hr-neon-pink", value: 60, prefix: "15–", suffix: "", label: "Minute Rounds", accent: "#FF2C8C", type: "number" },
    { icon: Baby, glow: "hr-neon-orange", value: 8, prefix: "", suffix: "+", label: "Ages", accent: "#FF7A00", type: "number" },
    { icon: Fire, glow: "hr-neon-purple", text: "1000s", label: "Of Memorable Nights", accent: "#A742FF", type: "text" },
  ];
  return (
    <section className="relative py-14 md:py-24 border-t border-white/10">
      <div className="max-w-[1400px] mx-auto px-4 md:px-8">
        <Reveal>
          <h2 className="text-center font-display uppercase text-3xl md:text-4xl tracking-[0.02em] text-white">Why Everyone <span className="text-brandpink hr-text-glow-pink">Loves It.</span></h2>
        </Reveal>
        <div className="mt-10 md:mt-12 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-5 md:gap-6">
          {items.map((it, i) => {
            const Ic = it.icon;
            const isLast = i === items.length - 1;
            return (
              <Reveal key={it.label} delay={i * 0.08} className={isLast ? "col-span-2 sm:col-span-3 md:col-span-1" : ""}>
                <div className="flex flex-col items-center text-center gap-3 md:gap-4" data-testid={`stat-${i}`}>
                  <div className={`h-20 w-20 md:h-24 md:w-24 rounded-full grid place-items-center ${it.glow} bg-[#0f0724]`}>
                    <Ic size={30} weight="duotone" style={{ color: it.accent }} />
                  </div>
                  <div>
                    <div className="font-anton text-3xl md:text-5xl leading-none" style={{ color: it.accent }}>
                      {it.type === "number" ? (
                        <Counter to={it.value} prefix={it.prefix} suffix={it.suffix} />
                      ) : (
                        it.text
                      )}
                    </div>
                    <div className="mt-2 font-sora text-[9.5px] md:text-[10px] uppercase tracking-[0.28em] text-white/60">{it.label}</div>
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// -------- Deck card (small, colored panel with animated 3D asterisk) --------
function DeckCard({ deck, index, onBuy }) {
  const Icon = deck.icon;
  const [tilt, setTilt] = useState({ x: 0, y: 0 });
  const cardRef = useRef(null);

  const onMove = (e) => {
    const el = cardRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const px = ((e.clientX - r.left) / r.width) - 0.5;
    const py = ((e.clientY - r.top) / r.height) - 0.5;
    setTilt({ x: py * -8, y: px * 8 });
  };
  const onLeave = () => setTilt({ x: 0, y: 0 });

  return (
    <motion.article
      ref={cardRef}
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.7, delay: index * 0.06, ease: [0.22, 1, 0.36, 1] }}
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      onClick={() => onBuy && onBuy(deck.key)}
      className="group relative aspect-[4/5] md:aspect-square lg:aspect-[6/5] rounded-xl overflow-hidden cursor-pointer"
      style={{
        background: `linear-gradient(160deg, ${deck.from} 0%, ${deck.to} 100%)`,
        boxShadow: `0 18px 44px rgba(${deck.glow}, 0.32), inset 0 0 0 1px rgba(255,255,255,0.06)`,
        transform: `perspective(800px) rotateX(${tilt.x}deg) rotateY(${tilt.y}deg)`,
        transition: "transform 0.2s cubic-bezier(0.22, 1, 0.36, 1)",
        transformStyle: "preserve-3d",
      }}
      data-testid={`deck-card-${index + 1}`}
    >
      {/* Classic AsteriskMark bottom-right (spinning, per-deck accent, mockup style) */}
      <div
        aria-hidden
        className="absolute -bottom-6 -right-4 md:-bottom-8 md:-right-6 z-0 opacity-40 group-hover:opacity-70 transition-opacity duration-500"
        style={{ color: deck.accent }}
      >
        <AsteriskMark size={140} spin />
      </div>

      {/* Bottom vignette for legibility over sparkle */}
      <div aria-hidden className="absolute inset-x-0 bottom-0 h-1/2 pointer-events-none z-0" style={{
        background: `linear-gradient(180deg, transparent, ${deck.to} 90%)`,
      }} />

      {/* Grain overlay */}
      <div className="absolute inset-0 grain opacity-25 pointer-events-none" />

      <div className="relative z-10 p-5 md:p-6 flex flex-col h-full" style={{ transform: "translateZ(30px)" }}>
        {/* Thin-ring circle icon top-left */}
        <div className="h-9 w-9 rounded-full grid place-items-center" style={{ border: "1.5px solid rgba(255,255,255,0.75)" }}>
          <Icon size={15} weight="regular" className="text-white" />
        </div>

        <div className="mt-auto pr-4">
          <div className="font-display uppercase text-3xl md:text-4xl leading-[0.92] tracking-tight text-white">
            {deck.name.split(" ").map((w, i) => <div key={i}>{w}</div>)}
          </div>
          <p className="mt-3 font-sora font-semibold text-[10.5px] md:text-[11px] uppercase tracking-[0.15em] text-white/85 leading-snug">
            {deck.prompt}
          </p>
        </div>
      </div>
    </motion.article>
  );
}

function FourDecks({ onAddToCart, price }) {
  return (
    <section id="how" className="relative border-t border-white/10 overflow-hidden">
      {/* Deep dark background with subtle purple/blue glow */}
      <div aria-hidden className="absolute inset-0 pointer-events-none" style={{
        background: "radial-gradient(60% 50% at 15% 30%, rgba(123,44,255,0.14), transparent 65%), radial-gradient(50% 50% at 85% 80%, rgba(41,108,255,0.08), transparent 70%), #0D0620",
      }} />

      <div className="relative max-w-[1400px] mx-auto px-4 md:px-8 py-16 md:py-20 lg:py-16 grid md:grid-cols-12 gap-8 md:gap-10 lg:gap-12 items-center">
        {/* Left copy column */}
        <div className="md:col-span-5">
          <Reveal>
            <div className="font-sora font-semibold text-[10.5px] uppercase tracking-[0.32em] text-brandpink mb-5">How To Play</div>
          </Reveal>
          <Reveal delay={0.05}>
            <h2 className="font-display uppercase text-5xl md:text-6xl lg:text-7xl leading-[0.92] tracking-[-0.01em] text-white">
              Four Decks.<br />Zero Filter.
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-6 max-w-sm text-white/70 text-sm md:text-[15px] font-sans leading-relaxed">
              Draw a card. Beat the timer. Earn points. The group decides who survives - and who&apos;s getting screenshotted.
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <Magnetic strength={0.24} className="inline-block mt-8">
              <button
                onClick={onAddToCart}
                className="hr-cta h-12 md:h-14 px-5 md:px-7 rounded-lg text-white font-sora font-semibold text-[11px] md:text-xs uppercase tracking-[0.2em] md:tracking-[0.22em] flex items-center gap-2 whitespace-nowrap"
                data-testid="four-decks-add-to-cart"
              >
                <ShoppingCart size={14} weight="fill" />
                Add to Cart - ${Number(price).toFixed(2)}
              </button>
            </Magnetic>
          </Reveal>
        </div>

        {/* Right 2x2 deck grid */}
        <div className="md:col-span-7">
          <div className="grid grid-cols-2 gap-3 md:gap-3 lg:gap-4">
            {DECKS.map((d, i) => <DeckCard key={d.name} deck={d} index={i} onBuy={() => onAddToCart()} />)}
          </div>
        </div>
      </div>
    </section>
  );
}

// -------- What's Inside (exploded view) --------
function WhatsInside() {
  const parts = [
    { label: "Premium Box", top: "8%", left: "8%" },
    { label: "200+ Cards", top: "18%", right: "6%" },
    { label: "Score Pad", top: "48%", right: "4%" },
    { label: "Rule Book", top: "72%", right: "8%" },
    { label: "Timer", top: "82%", left: "10%" },
  ];
  return (
    <section id="box" className="relative border-t border-white/10 py-16 md:py-28 overflow-hidden">
      <div className="max-w-[1400px] mx-auto px-4 md:px-8 grid lg:grid-cols-12 gap-10 items-center">
        {/* Left: exploded product view */}
        <div className="lg:col-span-7 relative">
          <div className="relative aspect-square rounded-2xl overflow-hidden hr-neon-purple border border-brandpurple/30">
            {/* Glow bg */}
            <div className="absolute inset-0" style={{ background: "radial-gradient(60% 60% at 50% 50%, rgba(123,44,255,0.35), transparent 70%)" }} />
            <motion.img
              initial={{ opacity: 0, scale: 0.9, rotate: -6 }}
              whileInView={{ opacity: 1, scale: 1, rotate: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
              src={BOX_IMG}
              alt="House Rules game box"
              className="absolute inset-0 w-full h-full object-cover"
            />
            {/* Labels with arrows - pill style with glass bg + neon pink border */}
            {parts.map((p, i) => (
              <motion.div
                key={p.label}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.4 + i * 0.1 }}
                style={{ top: p.top, left: p.left, right: p.right }}
                className="absolute hidden md:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full backdrop-blur-md bg-black/55 border border-brandpink/50 font-sora font-semibold text-[9.5px] uppercase tracking-[0.22em] text-brandpink shadow-[0_0_18px_rgba(255,44,140,0.35)] whitespace-nowrap"
              >
                <span aria-hidden className="text-brandpink">→</span>
                <span className="text-white/95">{p.label}</span>
              </motion.div>
            ))}
          </div>
        </div>
        {/* Right: copy */}
        <div className="lg:col-span-5">
          <Reveal>
            <div className="font-mono text-[10px] uppercase tracking-[0.35em] text-brandpink mb-4">What&apos;s Inside</div>
          </Reveal>
          <Reveal delay={0.05}>
            <h2 className="font-display uppercase text-5xl md:text-6xl leading-[0.88] tracking-[-0.01em]">
              The Ultimate<br />Recipe For <span className="italic font-editorial text-brandpink hr-text-glow-pink">Chaos.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-6 max-w-md font-sans text-white/70 leading-relaxed">
              Four decks. One box. Endless ammo for any occasion - game night, holidays, road trip, or that awkward family dinner that needed saving.
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <ul className="mt-7 space-y-3 font-sans text-white/85 text-sm">
              {[
                "200+ premium gloss cards",
                "4 decks · Physical, House Charades, Know Your House, Truth or Lie",
                "2+ players · ages 8+",
                "15–60 minute rounds",
                "Easy to learn. Impossible to forget.",
              ].map((f) => (
                <li key={f} className="flex items-start gap-3">
                  <CheckCircle size={18} weight="fill" className="text-brandpink mt-0.5 shrink-0" />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

// -------- Host + Trailer --------
function HostTrailer({ onWatch, onAddToCart, price }) {
  return (
    <section id="host" className="relative border-t border-white/10 py-16 md:py-24">
      <div className="max-w-[1400px] mx-auto px-4 md:px-8 grid lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-5">
          <Reveal>
            <div className="font-sora font-semibold text-[10.5px] uppercase tracking-[0.32em] text-brandpink mb-3">
              Your Host · As Seen On Tubi
            </div>
          </Reveal>
          <Reveal delay={0.05}>
            <h3 className="font-display uppercase text-5xl md:text-6xl leading-[0.88] tracking-[-0.01em]">
              Hosted by Laro <span className="italic font-editorial text-brandpink hr-text-glow-pink">Benz.</span>
            </h3>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-5 text-white/70 max-w-md text-sm md:text-base font-sans leading-relaxed">
              Comedian, host and chaos coordinator. Laro brings the energy of his Tubi show straight into your living room - every card written, tested, and approved on set.
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <blockquote
              className="mt-6 pl-4 border-l-2 border-brandpink italic font-editorial text-brandpink text-lg md:text-xl leading-snug max-w-md hr-text-glow-pink"
              data-testid="host-quote"
            >
              &ldquo;This isn&apos;t a game. It&apos;s a true test of who your friends really are.&rdquo;
            </blockquote>
          </Reveal>
          <Reveal delay={0.2}>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Magnetic strength={0.2}>
                <button
                  onClick={onAddToCart}
                  className="hr-cta h-12 md:h-13 pl-5 pr-5 md:pl-6 md:pr-6 rounded-full text-white font-sora font-semibold text-[11px] md:text-xs uppercase tracking-[0.2em] flex items-center gap-2 whitespace-nowrap"
                  data-testid="host-add-to-cart"
                >
                  <ShoppingCart size={14} weight="fill" />
                  Add to Cart - {fmt(price)}
                </button>
              </Magnetic>
              <button
                onClick={onWatch}
                className="hr-outline h-12 md:h-13 pl-5 pr-3 md:pl-6 md:pr-4 rounded-full text-white/95 font-sora font-semibold text-[11px] md:text-xs uppercase tracking-[0.2em] flex items-center gap-2 md:gap-3 whitespace-nowrap"
                data-testid="host-watch-button"
              >
                Watch Trailer
                <span
                  className="h-8 w-8 md:h-9 md:w-9 rounded-full grid place-items-center shrink-0"
                  style={{ backgroundColor: "#FFFFFF", color: "#080412" }}
                >
                  <Play size={14} weight="fill" style={{ marginLeft: "1px" }} />
                </span>
              </button>
            </div>
          </Reveal>
        </div>
        <div className="lg:col-span-7">
          <Reveal delay={0.1}>
            <a
              href="https://tubitv.com/series/300021863/house-rules"
              target="_blank" rel="noopener noreferrer"
              className="group relative block aspect-[16/9] rounded-2xl overflow-hidden border border-brandpink/30 hr-neon-pink"
              data-testid="host-video-link"
            >
              <img src={HOST_IMG} alt="Laro Benz - House Rules trailer" className="w-full h-full object-cover" loading="lazy" />
              <div className="absolute inset-0" style={{ background: "radial-gradient(70% 60% at 50% 50%, transparent 30%, rgba(8,4,18,0.7) 100%)" }} />
              <div className="absolute inset-0 grid place-items-center">
                <motion.div
                  whileHover={{ scale: 1.12 }}
                  transition={{ type: "spring", stiffness: 300, damping: 15 }}
                  className="h-20 w-20 rounded-full bg-brandpink grid place-items-center shadow-[0_10px_60px_rgba(255,44,140,0.7)]"
                >
                  <Play size={26} weight="fill" className="ml-1 text-white" />
                </motion.div>
              </div>
              <div className="absolute bottom-5 left-5 right-5 flex items-end justify-between">
                <div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/70">Now Streaming</div>
                  <div className="font-display uppercase text-xl leading-none mt-1 text-white">House Rules · Season 1</div>
                </div>
                <div
                  className="font-sans font-black lowercase leading-none tracking-[-0.04em]"
                  style={{ color: "#FA541C", fontSize: "22px", fontFamily: "'Sora', ui-sans-serif, system-ui, sans-serif" }}
                  aria-label="tubi"
                >
                  tubi
                </div>
              </div>
            </a>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

// -------- Meet the Four Decks - showcase all decks with one CTA --------
const DECK_SHOWCASE = [
  {
    name: "Physical",
    tag: "Movement",
    prompt: "Get off the couch.",
    body: "Lemon-on-spoon races, plank-offs, and stunts your group chat will replay for years.",
    img: CARD_A,
    accent: "#B57BFF",
    glow: "hr-neon-purple",
  },
  {
    name: "House Charades",
    tag: "Charades",
    prompt: "Act it out, don't say it.",
    body: "Fold laundry. Lose a sock. Recreate Saturday morning. 45 seconds on the clock.",
    img: CARD_B,
    accent: "#FFB44E",
    glow: "hr-neon-orange",
  },
  {
    name: "Know Your House",
    tag: "Family",
    prompt: "How well do you know your people?",
    body: "Inside jokes. Real stories. Bet you'll learn something new about the people you live with.",
    img: CARD_C,
    accent: "#6DA6FF",
    glow: "hr-neon-blue",
  },
  {
    name: "Truth or Lie",
    tag: "Household",
    prompt: "Spill it or make it up.",
    body: "Hot takes. Wild stories. Can you bluff your way out - or read the room?",
    img: CARD_D,
    accent: "#FF7EBB",
    glow: "hr-neon-pink",
  },
];

function MeetTheDecks({ onAddToCart, price }) {
  return (
    <section className="relative border-t border-white/10 py-16 md:py-24 overflow-hidden" data-testid="meet-the-decks-section">
      <div aria-hidden className="absolute inset-0 pointer-events-none" style={{
        background: "radial-gradient(50% 40% at 20% 20%, rgba(123,44,255,0.12), transparent 65%), radial-gradient(45% 35% at 85% 80%, rgba(255,44,140,0.10), transparent 70%)",
      }} />
      <div className="relative max-w-[1400px] mx-auto px-4 md:px-8">
        <div className="text-center max-w-2xl mx-auto mb-10 md:mb-14">
          <Reveal>
            <div className="font-sora font-semibold text-[10.5px] uppercase tracking-[0.32em] text-brandpink mb-4">Meet The Decks</div>
          </Reveal>
          <Reveal delay={0.05}>
            <h2 className="font-display uppercase text-4xl md:text-5xl lg:text-6xl leading-[0.9] tracking-[-0.01em]">
              Four Decks. <span className="italic font-editorial text-brandpink hr-text-glow-pink">One Box.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-5 text-white/70 text-sm md:text-base font-sans leading-relaxed">
              Every deck has its own rules, energy, and inside jokes waiting to happen. Pick a color, beat the timer, ruin your friendships responsibly.
            </p>
          </Reveal>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
          {DECK_SHOWCASE.map((d, i) => (
            <motion.article
              key={d.name}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.6, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }}
              whileHover={{ y: -6 }}
              className={`relative rounded-2xl overflow-hidden hr-glass ${d.glow} flex flex-col`}
              data-testid={`meet-deck-${i + 1}`}
            >
              <div className="relative aspect-[4/5] overflow-hidden bg-black/40">
                <img src={d.img} alt={`${d.name} deck`} className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
                <div aria-hidden className="absolute inset-0" style={{
                  background: `linear-gradient(180deg, transparent 55%, rgba(8,4,18,0.9) 100%)`,
                }} />
                <div
                  className="absolute top-3 left-3 px-2.5 py-1 rounded-full bg-black/55 backdrop-blur border font-sora font-semibold text-[9.5px] uppercase tracking-[0.22em]"
                  style={{ color: d.accent, borderColor: `${d.accent}66` }}
                >
                  {d.tag}
                </div>
              </div>
              <div className="p-5 md:p-6 flex flex-col gap-3 flex-1">
                <h3 className="font-display uppercase text-2xl md:text-3xl leading-[0.95] tracking-tight text-white">{d.name}</h3>
                <p className="font-sora font-semibold text-[10.5px] uppercase tracking-[0.18em]" style={{ color: d.accent }}>
                  {d.prompt}
                </p>
                <p className="mt-1 font-sans text-sm text-white/70 leading-relaxed">{d.body}</p>
              </div>
            </motion.article>
          ))}
        </div>

        <div className="mt-10 md:mt-14 flex justify-center">
          <Magnetic strength={0.22}>
            <button
              onClick={onAddToCart}
              className="hr-cta h-12 md:h-14 px-6 md:px-8 rounded-full text-white font-sora font-semibold text-xs md:text-sm uppercase tracking-[0.18em] flex items-center gap-3 whitespace-nowrap"
              data-testid="meet-decks-buy-now"
            >
              <ShoppingCart size={16} weight="fill" />
              Buy The Box Now - {fmt(price)}
            </button>
          </Magnetic>
        </div>
      </div>
    </section>
  );
}


// -------- Two Ways In - 2-option bundle layout (Original + Buy 2 Gift 1) --------
function BundleBuilder({ onBuy, price }) {
  return (
    <section id="shop" className="relative border-t border-white/10 py-16 md:py-24 overflow-hidden">
      <div className="max-w-[1400px] mx-auto px-4 md:px-8">
        <div className="flex flex-wrap items-end justify-between gap-6 mb-10 md:mb-14">
          <Reveal>
            <h2 className="font-display uppercase text-4xl sm:text-5xl md:text-6xl lg:text-7xl leading-[0.9] tracking-[-0.01em]">
              Two Ways <span className="italic font-editorial text-brandpink hr-text-glow-pink">In.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="max-w-sm text-white/60 text-sm md:text-base leading-relaxed">
              Grab the box for yourself, or gift a couple you love. Both ship free anywhere in the US.
            </p>
          </Reveal>
        </div>

        <div className="grid md:grid-cols-2 gap-5 md:gap-8">
          {/* Option 1 - The Original */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="relative rounded-[24px] overflow-hidden hr-glass p-6 md:p-8 lg:p-10 flex flex-col"
            data-testid="bundle-original"
          >
            <div className="font-sora font-semibold text-[10px] uppercase tracking-[0.3em] text-brandpink mb-3">
              Solo Box · 200+ Cards · 4 Decks
            </div>
            <h3 className="font-display uppercase text-4xl md:text-5xl lg:text-6xl leading-[0.9] tracking-[-0.01em] break-words">
              The<br /><span className="italic font-editorial text-brandpink hr-text-glow-pink">Original.</span>
            </h3>
            <div className="mt-6 md:mt-8 aspect-square rounded-2xl overflow-hidden hr-neon-purple border border-brandpurple/30 bg-black/40">
              <img src={ORIGINAL_QUAD_IMG} alt="House Rules - all 4 decks" className="w-full h-full object-cover" loading="lazy" />
            </div>
            <ul className="mt-6 md:mt-8 space-y-2.5 text-white/80 text-sm">
              <li className="flex items-start gap-2.5"><CheckCircle size={16} weight="fill" className="text-brandpink mt-0.5 shrink-0" /> <span>One premium game box, ready for game night</span></li>
              <li className="flex items-start gap-2.5"><CheckCircle size={16} weight="fill" className="text-brandpink mt-0.5 shrink-0" /> <span>200+ cards across 4 decks</span></li>
              <li className="flex items-start gap-2.5"><CheckCircle size={16} weight="fill" className="text-brandpink mt-0.5 shrink-0" /> <span>Free US shipping, ships in 24h from LA</span></li>
            </ul>
            <div className="mt-6 md:mt-8 flex items-end justify-between border-t border-white/10 pt-5 md:pt-6 gap-3">
              <div className="min-w-0">
                <div className="font-anton text-4xl md:text-5xl leading-none text-white">{fmt(price)}</div>
                <div className="font-sora font-semibold uppercase text-[9.5px] tracking-[0.25em] text-white/50 mt-1.5">One-Time</div>
              </div>
              <Magnetic strength={0.2}>
                <button
                  onClick={() => onBuy("original")}
                  className="hr-cta h-11 md:h-12 px-4 md:px-5 rounded-full text-white font-sora font-semibold text-[10.5px] md:text-[11px] uppercase tracking-[0.2em] flex items-center gap-2 whitespace-nowrap"
                  data-testid="bundle-buy-original"
                >
                  <ShoppingCart size={14} weight="fill" />
                  Add to Cart
                </button>
              </Magnetic>
            </div>
          </motion.div>

          {/* Option 2 - Buy 2, Gift One */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
            className="relative rounded-[24px] overflow-hidden p-6 md:p-8 lg:p-10 flex flex-col hr-neon-pink"
            style={{
              background:
                "radial-gradient(120% 90% at 30% 20%, #5B24C8 0%, #3B12A0 40%, #150540 100%)",
            }}
            data-testid="bundle-gift"
          >
            <div className="absolute top-4 right-4 md:top-6 md:right-6 bg-brandpink text-white font-sora font-semibold text-[9px] md:text-[10px] uppercase tracking-[0.25em] px-3 py-1.5 rounded-full whitespace-nowrap">
              Best Value · Save $15
            </div>
            <div className="font-sora font-semibold text-[10px] uppercase tracking-[0.3em] text-white/75 mb-3 pr-32 md:pr-40">
              2 Boxes Shipped · One Goes To Your Friend
            </div>
            <h3 className="font-display uppercase text-4xl md:text-5xl lg:text-6xl leading-[0.9] tracking-[-0.01em] text-white break-words">
              Buy 1,<br />Gift <span className="italic font-editorial text-brandpink hr-text-glow-pink">One.</span>
            </h3>
            <div className="mt-6 md:mt-8 relative aspect-square rounded-2xl overflow-hidden hr-neon-pink border border-brandpink/40 bg-black/40">
              <img src={ORIGINAL_QUAD_IMG} alt="House Rules - 2 boxes bundle" className="w-full h-full object-cover" loading="lazy" />
              {/* ×2 badge - you get 2 boxes */}
              <div className="absolute top-3 right-3 md:top-4 md:right-4 flex items-center gap-1.5 h-9 md:h-10 px-3 md:px-4 rounded-full bg-brandpink text-white font-anton text-lg md:text-xl leading-none shadow-[0_10px_30px_rgba(255,44,140,0.55)] border border-white/20">
                <span>×2</span>
                <span className="font-sora font-semibold text-[9px] md:text-[10px] uppercase tracking-[0.2em] pt-0.5">Boxes</span>
              </div>
            </div>
            <ul className="mt-6 md:mt-8 space-y-2.5 text-white/85 text-sm">
              <li className="flex items-start gap-2.5"><CheckCircle size={16} weight="fill" className="text-white mt-0.5 shrink-0" /> <span>1 × Original Game Box for you</span></li>
              <li className="flex items-start gap-2.5"><CheckCircle size={16} weight="fill" className="text-white mt-0.5 shrink-0" /> <span>1 × Bonus Box shipped to a friend</span></li>
              <li className="flex items-start gap-2.5"><CheckCircle size={16} weight="fill" className="text-white mt-0.5 shrink-0" /> <span>Save $15 · Bundle-only pricing</span></li>
            </ul>
            <div className="mt-6 md:mt-8 flex items-end justify-between border-t border-white/15 pt-5 md:pt-6 gap-3">
              <div className="min-w-0">
                <div className="flex items-baseline gap-2 md:gap-3 flex-wrap">
                  <div className="font-anton text-4xl md:text-5xl leading-none text-white">$83</div>
                  <div className="font-mono text-base md:text-lg text-white/50 line-through">$98</div>
                </div>
                <div className="font-sora font-semibold uppercase text-[9.5px] tracking-[0.25em] text-white/70 mt-1.5">Bundle · 2 Boxes</div>
              </div>
              <Magnetic strength={0.2}>
                <button
                  onClick={() => onBuy("gift")}
                  className="hr-cta h-11 md:h-12 px-4 md:px-5 rounded-full text-white font-sora font-semibold text-[10.5px] md:text-[11px] uppercase tracking-[0.2em] flex items-center gap-2 whitespace-nowrap"
                  data-testid="bundle-buy-gift"
                >
                  <ShoppingCart size={14} weight="fill" />
                  Add to Cart
                </button>
              </Magnetic>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

// -------- Social Proof --------
const UGC = [
  { handle: "sarah_11", name: "Sarah Miller",     platform: "instagram", avatar: "#FF2C8C", quote: "We lost all dignity in 10 minutes 😂",         likes: "12.4k", comments: 284,  card: 3, tag: "Truth or Lie" },
  { handle: "mikealwaywins", name: "Mike Rivera", platform: "tiktok",    avatar: "#7B2CFF", quote: "This game is absolutely unhinged 💀",         likes: "8.9k",  comments: 421,  card: 0, tag: "Physical" },
  { handle: "jaralyn.j", name: "Jaralyn J.",      platform: "instagram", avatar: "#FF7A00", quote: "Turned game night into a full-blown movie 🎬", likes: "5.7k",  comments: 96,   card: 1, tag: "Charades" },
  { handle: "tylerjames", name: "Tyler J.",       platform: "tiktok",    avatar: "#296CFF", quote: "Best gift I've ever given my friends ✨",       likes: "22.1k", comments: 803,  card: 2, tag: "Family" },
  { handle: "natalieee", name: "Natalie Chen",    platform: "instagram", avatar: "#FF2C8C", quote: "Every round is pure chaos 🔥",                 likes: "3.2k",  comments: 74,   card: 3, tag: "Truth or Lie" },
  { handle: "justincredibfe", name: "Justin Lee", platform: "tiktok",    avatar: "#B57BFF", quote: "Laughs we didn't know we needed",              likes: "15.6k", comments: 512,  card: 0, tag: "Physical" },
];
function SocialProof() {
  const scrollerRef = useRef(null);
  const scrollBy = (dir) => {
    scrollerRef.current?.scrollBy({ left: dir * 320, behavior: "smooth" });
  };
  const CARDS = [UGC_A, UGC_B, UGC_C, UGC_D];
  return (
    <section className="relative border-t border-white/10 py-16 md:py-24">
      <div className="max-w-[1400px] mx-auto px-4 md:px-8">
        <div className="flex flex-wrap items-end justify-between gap-6 mb-10">
          <Reveal>
            <div>
              <div className="font-sora font-semibold text-[10.5px] uppercase tracking-[0.32em] text-brandpink mb-3">#HouseRulesNight</div>
              <h2 className="font-display uppercase text-4xl md:text-5xl lg:text-6xl tracking-[-0.01em]">
                Real People. <span className="text-brandpink hr-text-glow-pink">Real Nights.</span>
              </h2>
            </div>
          </Reveal>
          <div className="flex gap-2">
            <button onClick={() => scrollBy(-1)} className="h-11 w-11 rounded-full hr-outline grid place-items-center text-white" aria-label="Scroll left" data-testid="social-scroll-left"><CaretLeft size={16} weight="bold" /></button>
            <button onClick={() => scrollBy(1)} className="h-11 w-11 rounded-full hr-outline grid place-items-center text-white" aria-label="Scroll right" data-testid="social-scroll-right"><CaretRight size={16} weight="bold" /></button>
          </div>
        </div>
        <div ref={scrollerRef} className="flex gap-4 md:gap-5 overflow-x-auto snap-x snap-mandatory no-scrollbar pb-4 -mx-4 px-4 md:mx-0 md:px-0">
          {UGC.map((u, i) => {
            const isTikTok = u.platform === "tiktok";
            const PlatformIcon = isTikTok ? TiktokLogo : InstagramLogo;
            const initials = u.name.split(" ").map((n) => n[0]).slice(0, 2).join("");
            return (
              <motion.article
                key={i}
                whileHover={{ y: -6 }}
                className="flex-shrink-0 w-[80%] sm:w-[46%] md:w-[32%] lg:w-[24%] snap-start rounded-2xl overflow-hidden bg-[#0E0722] border border-white/10 relative"
                data-testid={`ugc-${i}`}
              >
                {/* Social header - avatar + handle + platform icon */}
                <div className="flex items-center gap-2.5 px-3 py-2.5 border-b border-white/8">
                  <div
                    className="h-9 w-9 rounded-full grid place-items-center text-white font-sora font-bold text-[10.5px] tracking-wide shrink-0"
                    style={{ background: `linear-gradient(135deg, ${u.avatar}, ${u.avatar}88)`, boxShadow: `0 0 12px ${u.avatar}66` }}
                    aria-hidden
                  >
                    {initials}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-sora font-semibold text-[12px] text-white leading-tight truncate">@{u.handle}</div>
                    <div className="font-sora text-[10px] text-white/50 leading-tight">{u.tag} · sponsored</div>
                  </div>
                  <PlatformIcon size={16} weight="fill" className={isTikTok ? "text-white" : "text-brandpink"} />
                  <DotsThreeVertical size={16} weight="bold" className="text-white/50" />
                </div>

                {/* Media - deck card in a 4:5 frame */}
                <div className="relative aspect-[4/5] bg-black">
                  <img src={CARDS[u.card % CARDS.length]} alt={`House Rules game night - ${u.name}`} className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
                  {/* Post overlay - subtle vignette */}
                  <div aria-hidden className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(0,0,0,0.2) 0%, transparent 25%, transparent 60%, rgba(0,0,0,0.35) 100%)" }} />
                  {/* Sound badge for TikTok */}
                  {isTikTok && (
                    <div className="absolute bottom-3 right-3 flex items-center gap-1.5 px-2 py-1 rounded-full bg-black/55 backdrop-blur border border-white/15 font-sora text-[9.5px] text-white/90">
                      <span className="h-1.5 w-1.5 rounded-full bg-white animate-pulse" />
                      Original Sound
                    </div>
                  )}
                </div>

                {/* Reactions row */}
                <div className="flex items-center gap-4 px-3 pt-3">
                  <div className="flex items-center gap-1.5 text-brandpink" data-testid={`ugc-${i}-heart`}>
                    <Heart size={20} weight="fill" />
                    <span className="font-sora font-semibold text-[12px] text-white">{u.likes}</span>
                  </div>
                  <div className="flex items-center gap-1.5 text-white/85">
                    <ChatCircleDots size={19} weight="bold" />
                    <span className="font-sora text-[12px]">{u.comments}</span>
                  </div>
                  <div className="text-white/85">
                    <PaperPlaneRight size={19} weight="bold" />
                  </div>
                  <div className="ml-auto text-white/85">
                    <BookmarkSimple size={19} weight="bold" />
                  </div>
                </div>

                {/* Caption */}
                <div className="px-3 pt-2 pb-4">
                  <p className="font-sans text-[13px] leading-snug text-white/90">
                    <span className="font-sora font-semibold text-white">@{u.handle}</span> {u.quote}
                  </p>
                  <div className="mt-1.5 font-sora text-[10.5px] text-brandpink/90">#HouseRulesNight #GameNight #ChaosOnly</div>
                </div>
              </motion.article>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// -------- Testimonials (written reviews) --------
const TESTIMONIALS = [
  {
    name: "Priya S.",
    location: "Brooklyn, NY",
    quote: "We opened the box on Friday and by Saturday my roommate wasn't speaking to me. 10/10, obsessed.",
    deck: "Truth or Lie",
    avatar: "#FF2C8C",
  },
  {
    name: "Marcus T.",
    location: "Austin, TX",
    quote: "The Physical deck at a birthday party turned six adults into eight-year-olds. Genuinely the loudest my apartment has ever been.",
    deck: "Physical",
    avatar: "#7B2CFF",
  },
  {
    name: "Jamie & Kenzo",
    location: "Chicago, IL",
    quote: "House Charades is our new date-night default. We've broken it out for every single guest in the last month.",
    deck: "Charades",
    avatar: "#FF7A00",
  },
  {
    name: "Aisha R.",
    location: "Seattle, WA",
    quote: "Know Your House made us find out my brother has been lying about his favorite band for 15 years. Worth every dollar.",
    deck: "Family",
    avatar: "#296CFF",
  },
  {
    name: "Danny C.",
    location: "Los Angeles, CA",
    quote: "Gifted three boxes for the holidays and got asked to bring one to every house I visited. Best gift I've ever given.",
    deck: "Gift Edition",
    avatar: "#A742FF",
  },
];

function Testimonials() {
  return (
    <section id="reviews" className="relative border-t border-white/10 py-16 md:py-24" data-testid="testimonials-section">
      <div className="max-w-[1400px] mx-auto px-4 md:px-8">
        <div className="text-center max-w-3xl mx-auto mb-12 md:mb-16">
          <Reveal>
            <div className="font-sora font-semibold text-[10.5px] uppercase tracking-[0.32em] text-brandpink mb-4">
              Word of Mouth · Verified Buyers
            </div>
          </Reveal>
          <Reveal delay={0.05}>
            <h2 className="font-display uppercase text-4xl sm:text-5xl md:text-6xl lg:text-7xl leading-[0.9] tracking-[-0.01em]">
              People Who&apos;ve<br />
              <span className="italic font-editorial text-brandpink hr-text-glow-pink">Ruined Friendships.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="mt-6 inline-flex items-center gap-3 rounded-full hr-glass px-4 py-2.5" data-testid="testimonials-rating">
              <div className="flex items-center gap-0.5" aria-label="4.9 out of 5 stars">
                {[0, 1, 2, 3, 4].map((i) => (
                  <Star key={i} size={16} weight="fill" className="text-brandpink" />
                ))}
              </div>
              <span className="font-anton text-lg leading-none text-white">4.9 / 5</span>
              <span className="w-px h-4 bg-white/20" />
              <span className="font-sora font-semibold text-[10.5px] uppercase tracking-[0.25em] text-white/70">
                1,240+ Households
              </span>
            </div>
          </Reveal>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 md:gap-5">
          {TESTIMONIALS.map((t, i) => {
            const initials = t.name.split(" ").map((n) => n[0]).slice(0, 2).join("");
            const large = i === 0 || i === 4;
            return (
              <motion.article
                key={t.name}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.6, delay: i * 0.06, ease: [0.22, 1, 0.36, 1] }}
                whileHover={{ y: -4 }}
                className={`relative rounded-2xl hr-glass p-6 md:p-7 flex flex-col ${
                  large ? "xl:col-span-1 lg:col-span-1 sm:col-span-2 lg:!col-span-1" : ""
                }`}
                data-testid={`testimonial-${i + 1}`}
              >
                <div className="flex items-center gap-0.5 mb-3" aria-label="5 stars">
                  {[0, 1, 2, 3, 4].map((s) => (
                    <Star key={s} size={12} weight="fill" className="text-brandpink" />
                  ))}
                </div>
                <p className="font-sans text-[15px] md:text-base text-white/90 leading-relaxed flex-1">
                  &ldquo;{t.quote}&rdquo;
                </p>
                <div className="mt-6 pt-5 border-t border-white/10 flex items-center gap-3">
                  <div
                    className="h-10 w-10 rounded-full grid place-items-center text-white font-sora font-bold text-[11px] tracking-wide shrink-0"
                    style={{ background: `linear-gradient(135deg, ${t.avatar}, ${t.avatar}88)`, boxShadow: `0 0 12px ${t.avatar}66` }}
                    aria-hidden
                  >
                    {initials}
                  </div>
                  <div className="min-w-0">
                    <div className="font-sora font-semibold text-[13px] text-white truncate">{t.name}</div>
                    <div className="font-sora text-[10px] uppercase tracking-[0.22em] text-white/50 truncate">
                      {t.location} · {t.deck}
                    </div>
                  </div>
                </div>
              </motion.article>
            );
          })}
        </div>
      </div>
    </section>
  );
}


// -------- FAQ --------
const FAQ = [
  { q: "How do you play House Rules?", a: "Draw a card, follow the prompt, and beat the timer. The group decides who wins each round. First to 10 points wins." },
  { q: "How long does a game take?", a: "Between 15 and 60 minutes, depending on the deck and how many players." },
  { q: "How many players can play?", a: "2 or more. There's no upper limit - the more people, the more chaos." },
  { q: "What's the age recommendation?", a: "8+ on the box, but Truth or Lie and House Charades hit harder with an older group." },
  { q: "Is it suitable for kids?", a: "Yes - pick the Physical and Charades decks for family-friendly nights. Save Truth or Lie for adults." },
  { q: "Do you ship internationally?", a: "Currently US free shipping. International rates calculated at checkout." },
];
function FaqSection() {
  const half = Math.ceil(FAQ.length / 2);
  const left = FAQ.slice(0, half);
  const right = FAQ.slice(half);
  return (
    <section id="faq" className="relative border-t border-white/10 py-16 md:py-24">
      <div className="max-w-[1400px] mx-auto px-4 md:px-8">
        <Reveal>
          <h2 className="text-center font-display uppercase text-4xl md:text-5xl lg:text-6xl tracking-[-0.01em]">
            Questions? <span className="text-brandpink hr-text-glow-pink">We Got Answers.</span>
          </h2>
        </Reveal>
        <div className="mt-12 grid md:grid-cols-2 gap-4 md:gap-6">
          {[left, right].map((col, ci) => (
            <Accordion type="single" collapsible key={ci} className="space-y-3">
              {col.map((f, i) => (
                <AccordionItem key={i} value={`col-${ci}-item-${i}`} className="rounded-2xl overflow-hidden hr-glass border-0" data-testid={`faq-item-${ci}-${i}`}>
                  <AccordionTrigger className="px-5 py-4 text-left font-sora font-medium text-white hover:no-underline text-sm md:text-base">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="px-5 pb-4 font-sans text-white/65 text-sm leading-relaxed">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          ))}
        </div>
      </div>
    </section>
  );
}

// -------- Footer --------
function Footer() {
  const [email, setEmail] = useState("");
  const [subscribed, setSubscribed] = useState(false);
  const submit = (e) => {
    e.preventDefault();
    if (email && email.includes("@")) setSubscribed(true);
  };
  return (
    <footer className="relative border-t border-white/10 pt-16 pb-8 bg-[#080412]">
      <div className="max-w-[1400px] mx-auto px-4 md:px-8">
        <div className="grid grid-cols-2 md:grid-cols-12 gap-8 md:gap-10">
          {/* Logo + tubi + bio - spans full width on mobile */}
          <div className="col-span-2 md:col-span-4">
            <img src={LOGO_IMG} alt="House Rules" className="h-14 w-auto mb-5 rounded-lg" />
            <div className="flex items-center gap-2.5 text-sm">
              <span
                className="font-sans font-black lowercase leading-none tracking-[-0.04em]"
                style={{ color: "#FA541C", fontSize: "30px", fontFamily: "'Sora', ui-sans-serif, system-ui, sans-serif" }}
                aria-label="tubi"
              >
                tubi
              </span>
              <span className="font-sora font-semibold text-[10px] uppercase tracking-[0.3em] text-white/55">Presents</span>
            </div>
            <p className="mt-5 font-sans text-sm text-white/55 max-w-xs leading-relaxed">
              Hosted by Laro Benz. Game designed and produced by Revurge Corporation, Los Angeles.
            </p>
          </div>

          {/* SHOP */}
          <div className="md:col-span-2">
            <div className="font-sora font-semibold text-[10px] uppercase tracking-[0.3em] text-brandpink mb-4">Shop</div>
            <ul className="space-y-3 font-sans text-sm text-white/75">
              <li><a href="#shop" className="hover:text-white">All Products</a></li>
              <li><a href="#shop" className="hover:text-white">Bundles</a></li>
              <li><a href="#shop" className="hover:text-white">Gift Edition</a></li>
            </ul>
          </div>

          {/* INFO */}
          <div className="md:col-span-2">
            <div className="font-sora font-semibold text-[10px] uppercase tracking-[0.3em] text-brandpink mb-4">Info</div>
            <ul className="space-y-3 font-sans text-sm text-white/75">
              <li><a href="#how" className="hover:text-white">How To Play</a></li>
              <li><a href="#host" className="hover:text-white">About</a></li>
              <li><a href="#faq" className="hover:text-white">FAQ</a></li>
            </ul>
          </div>

          {/* JOIN THE CHAOS + FOLLOW (stacked on desktop, side-by-side on mobile) */}
          <div className="col-span-1 md:col-span-4">
            <div className="font-sora font-semibold text-[10px] uppercase tracking-[0.3em] text-brandpink mb-4">Join The Chaos</div>
            <p className="hidden md:block font-sans text-sm text-white/60 mb-4 max-w-xs">
              Get updates, deals, and exclusive content.
            </p>
            <form onSubmit={submit} className="flex items-center gap-2" data-testid="newsletter-form">
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                className="flex-1 min-w-0 h-11 px-4 rounded-full bg-white/[0.05] border border-white/15 focus:border-brandpink focus:outline-none font-sans text-sm text-white placeholder-white/40"
                data-testid="newsletter-input"
              />
              <button type="submit" className="hr-cta h-11 w-11 rounded-full grid place-items-center shrink-0" data-testid="newsletter-submit" aria-label="Subscribe">
                <PaperPlaneTilt size={14} weight="fill" />
              </button>
            </form>
            {subscribed && (
              <div className="mt-3 font-mono text-[10px] uppercase tracking-[0.2em] text-brandpink">Subscribed ✓ Welcome to the chaos.</div>
            )}
          </div>

          {/* SOCIALS - side-by-side with Join on mobile, own row on desktop */}
          <div className="col-span-1 md:col-span-12">
            <div className="font-sora font-semibold text-[10px] uppercase tracking-[0.3em] text-brandpink mb-4">Follow</div>
            <div className="flex flex-nowrap items-center gap-2.5 md:gap-3" data-testid="footer-socials">
              {[
                { label: "Instagram", Icon: InstagramLogo, href: "#" },
                { label: "TikTok", Icon: TiktokLogo, href: "#" },
                { label: "YouTube", Icon: YoutubeLogo, href: "#" },
                { label: "Discord", Icon: DiscordLogo, href: "#" },
                { label: "X", Icon: XLogo, href: "#" },
              ].map((s) => (
                <a
                  key={s.label}
                  href={s.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="h-10 w-10 rounded-full hr-outline grid place-items-center text-white/70 hover:text-brandpink hover:border-brandpink transition-colors shrink-0"
                  aria-label={s.label}
                >
                  <s.Icon size={16} weight="fill" />
                </a>
              ))}
            </div>
          </div>
        </div>

        {/* Legal row */}
        <div className="mt-14 flex flex-col md:flex-row items-center justify-between gap-4 border-t border-white/8 pt-6">
          <div className="font-sora text-[10px] uppercase tracking-[0.28em] text-white/40">
            © {new Date().getFullYear()} House Rules · A Wall of Entertainment Production
          </div>
          <div className="flex gap-6 font-sans text-xs text-white/50">
            <Link to="/shipping" className="hover:text-white">Shipping</Link>
            <Link to="/returns" className="hover:text-white">Returns</Link>
            <Link to="/terms" className="hover:text-white">Terms &amp; Conditions</Link>
            <Link to="/privacy" className="hover:text-white">Privacy</Link>
            <Link to="/orders/track" className="hover:text-white">Track Order</Link>
          </div>
        </div>

        {/* WoE production credit */}
        <a href="https://wallofentertainment.com" target="_blank" rel="noopener noreferrer" className="mt-6 pt-6 border-t border-white/5 flex items-center justify-center gap-4 group" data-testid="footer-woe-credit">
          <span className="font-mono text-[10px] uppercase tracking-[0.35em] text-white/40 group-hover:text-white/70">Produced By</span>
          <img src={WOE_LOGO} alt="Wall of Entertainment" className="h-6 w-auto rounded invert opacity-70 group-hover:opacity-100 transition-opacity" />
        </a>
      </div>
    </footer>
  );
}

// -------- WoE Production Credit - framed container with signature spinning asterisk --------
function WoeStrip() {
  return (
    <section className="relative border-t border-white/10 py-14 md:py-20 overflow-hidden">
      <div className="max-w-[1400px] mx-auto px-4 md:px-8">
        <a
          href="https://wallofentertainment.com"
          target="_blank"
          rel="noopener noreferrer"
          className="group relative block rounded-[24px] md:rounded-[32px] overflow-hidden border border-brandpink/25 hover:border-brandpink/60 transition-colors"
          data-testid="woe-production-strip"
          style={{
            background:
              "radial-gradient(60% 100% at 15% 50%, rgba(123,44,255,0.18), transparent 60%), radial-gradient(50% 100% at 85% 50%, rgba(255,44,140,0.12), transparent 60%), linear-gradient(180deg, #100826 0%, #0A0518 100%)",
            boxShadow:
              "0 0 0 1px rgba(255,44,140,0.15) inset, 0 20px 70px rgba(123,44,255,0.16)",
          }}
        >
          {/* Grain overlay */}
          <div aria-hidden className="absolute inset-0 grain opacity-25 pointer-events-none" />

          {/* Signature spinning asterisk (right side, matches Aug 7 design) */}
          <div
            aria-hidden
            className="absolute -right-10 md:-right-16 top-1/2 -translate-y-1/2 text-brandpink/40 group-hover:text-brandpink/70 transition-colors"
          >
            <AsteriskMark size={220} spin className="hidden md:block" />
            <AsteriskMark size={140} spin className="md:hidden" />
          </div>

          <div className="relative flex items-center gap-4 md:gap-10 px-5 md:px-10 py-8 md:py-12">
            {/* Left: label */}
            <div className="flex-shrink-0">
              <div className="font-sora font-semibold text-[10px] md:text-[11px] uppercase tracking-[0.32em] text-white/75 leading-tight">
                A Show<br className="md:hidden" /> Produced By
              </div>
            </div>

            {/* WoE logo */}
            <div className="flex-1 min-w-0">
              <img
                src={WOE_LOGO}
                alt="Wall of Entertainment"
                className="h-8 md:h-12 lg:h-14 w-auto opacity-95 group-hover:opacity-100 transition-opacity"
                style={{ filter: "brightness(0) invert(1) drop-shadow(0 0 22px rgba(255,44,140,0.35))" }}
              />
            </div>
          </div>
        </a>
      </div>
    </section>
  );
}

// -------- Sticky bottom CTA --------
function StickyCTA({ visible, onAddToCart, count, price }) {
  return (
    <div
      className="fixed bottom-4 sm:bottom-5 left-0 right-0 z-40 hidden md:flex justify-center pointer-events-none"
      aria-hidden={!visible}
      data-testid="sticky-cta"
    >
      <motion.div
        initial={false}
        animate={visible ? { y: 0, opacity: 1 } : { y: 80, opacity: 0 }}
        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
        className="pointer-events-auto"
        style={{ pointerEvents: visible ? "auto" : "none" }}
      >
        <Magnetic strength={0.18}>
          {count > 0 ? (
            <Link to="/cart" className="hr-cta flex items-center gap-2 h-12 px-5 rounded-full text-white font-sora text-[11px] uppercase tracking-[0.22em] font-semibold whitespace-nowrap">
              <ShoppingCart size={14} weight="fill" /> View Cart · {count}
            </Link>
          ) : (
            <button onClick={onAddToCart} className="hr-cta flex items-center gap-2 h-12 px-5 rounded-full text-white font-sora text-[11px] uppercase tracking-[0.22em] font-semibold whitespace-nowrap">
              <ShoppingCart size={14} weight="fill" /> Buy Now · {fmt(price)}
            </button>
          )}
        </Magnetic>
      </motion.div>
    </div>
  );
}

// -------- Mobile bottom bar (always visible on mobile) --------
function MobileBottomBar({ onAddToCart, count, price }) {
  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-40 md:hidden bg-[#080412]/95 backdrop-blur-xl border-t border-white/10 px-3 py-3"
      style={{ paddingBottom: "max(0.75rem, env(safe-area-inset-bottom))" }}
      data-testid="mobile-bottom-bar"
    >
      {count > 0 ? (
        <Link to="/cart" className="hr-cta flex items-center justify-center gap-2 h-12 rounded-full text-white font-sora text-[11px] uppercase tracking-[0.22em] font-semibold">
          <ShoppingCart size={14} weight="fill" /> View Cart · {count}
        </Link>
      ) : (
        <button onClick={onAddToCart} className="hr-cta w-full flex items-center justify-center gap-2 h-12 rounded-full text-white font-sora text-[11px] uppercase tracking-[0.22em] font-semibold">
          <ShoppingCart size={14} weight="fill" /> Buy Now - {fmt(price)}
        </button>
      )}
    </div>
  );
}

// -------- Main Landing --------
export default function Landing() {
  const { addItem, count, setOpen } = useCart();
  const [menuOpen, setMenuOpen] = useState(false);
  const [showSticky, setShowSticky] = useState(false);
  const [products, setProducts] = useState([]);

  useEffect(() => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/products`;
    fetch(url).then((r) => (r.ok ? r.json() : [])).then((d) => Array.isArray(d) && setProducts(d)).catch(() => {});
  }, []);

  const primary = useMemo(() => products[0], [products]);
  const price = primary?.price || 49;

  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY;
      const vh = window.innerHeight;
      const doc = document.documentElement;
      const distanceToBottom = doc.scrollHeight - (y + vh);
      // Show only between first fold (past ~1 viewport) and ~1.3 viewports before footer end
      const pastHero = y > vh * 0.9;
      const notNearFooter = distanceToBottom > vh * 1.1;
      setShowSticky(pastHero && notNearFooter);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, []);

  const handleBuyNow = () => { if (primary) { addItem(primary.id, 1); setOpen(true); } };
  const handleBuyBundle = (key) => {
    // Original = single box (price 49). Gift = bundle SKU (any non-49 priced product), fallback to 2x primary.
    if (key === "original") {
      if (primary) { addItem(primary.id, 1); setOpen(true); }
      return;
    }
    const bundle = products.find((p) => p.price && p.price !== 49);
    if (bundle) { addItem(bundle.id, 1); setOpen(true); }
    else if (primary) { addItem(primary.id, 2); setOpen(true); }
  };
  const handleWatch = () => {
    window.open("https://tubitv.com/series/300021863/house-rules", "_blank", "noopener,noreferrer");
  };

  return (
    <>
      <CursorGlow />
      <TopStrip />
      <Nav onOpenCart={() => setOpen(true)} onOpenMenu={() => setMenuOpen(true)} count={count} onBuyNow={handleBuyNow} price={price} />
      <MobileMenu open={menuOpen} onClose={() => setMenuOpen(false)} />
      <main className="relative z-10 pb-20 md:pb-0">
        <Hero onAddToCart={handleBuyNow} onWatch={handleWatch} price={price} />
        <WhyEveryoneLoves />
        <WhatsInside />
        <FourDecks onAddToCart={handleBuyNow} price={price} />
        <MeetTheDecks onAddToCart={handleBuyNow} price={price} />
        <HostTrailer onWatch={handleWatch} onAddToCart={handleBuyNow} price={price} />
        <SocialProof />
        <Testimonials />
        <WoeStrip />
        <BundleBuilder onBuy={handleBuyBundle} price={price} />
        <FaqSection />
      </main>
      <Footer />
      <StickyCTA visible={showSticky} onAddToCart={handleBuyNow} count={count} price={price} />
      <MobileBottomBar onAddToCart={handleBuyNow} count={count} price={price} />
    </>
  );
}
